/**
 * Deck MTGO exporter
 *
 * Exports decks in MTGO format: flat card list, blank line, commander(s) at end.
 */
import type { DeckZone } from '@/types/deck';

import type { ExportableDeckCard } from '../../decks/lib/deckImportExport';
import { formatCardLine } from './text';

// ============================================================================
// Types
// ============================================================================

export interface MtgoExportOptions {
  includeSetInfo?: boolean;
  includeMaybeboard?: boolean;
}

// ============================================================================
// Export
// ============================================================================

/**
 * Export a deck in MTGO format.
 *
 * Format: flat card list (main + sideboard), blank line, commander(s) at end.
 * Set codes are optional via `includeSetInfo`.
 *
 * Example:
 * ```
 * 1 Arcane Signet
 * 1 Sol Ring
 * ...
 *
 * 1 Ureni of the Unwritten
 * ```
 */
export function exportDeckListMtgo(cards: ExportableDeckCard[], _deckName?: string, options: MtgoExportOptions = {}): string {
  const { includeSetInfo = false, includeMaybeboard = false } = options;
  const lines: string[] = [];

  // Collect non-commander/companion cards (main + sideboard, optionally maybeboard)
  const mainZones: DeckZone[] = ['main', 'sideboard'];
  if (includeMaybeboard) mainZones.push('maybeboard');

  const mainCards = cards.filter((c) => mainZones.includes(c.zone)).sort((a, b) => a.card.name.localeCompare(b.card.name));

  for (const item of mainCards) {
    lines.push(formatCardLine(item, includeSetInfo));
  }

  // Commander/companion cards after blank line
  const commanderCards = cards
    .filter((c) => c.zone === 'commander' || c.zone === 'companion')
    .sort((a, b) => a.card.name.localeCompare(b.card.name));

  if (commanderCards.length > 0) {
    lines.push('');
    for (const item of commanderCards) {
      lines.push(formatCardLine(item, includeSetInfo));
    }
  }

  return lines.join('\n').trim();
}
